// src/pages/Admin/DossierInscription/EditDossierInscriptionModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaCloudUploadAlt,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileMedical,
  FaFileSignature,
  FaFolderOpen,
  FaGoogleDrive,
  FaIdCard,
  FaInfoCircle,
  FaLink,
  FaSave,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaTrashAlt,
  FaUpload,
  FaUser,
} from "react-icons/fa";

import {
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
  deleteDossierFile,
} from "./../../../services/dossierServices";

const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const DEFAULT_AVATAR = "/img/user_avatar.jpg";
const SIGNEE_FIELD = "formulaire_licence_signe";

const todayStr = () => new Date().toISOString().slice(0, 10);

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const toPublicUrl = (url = "") =>
  url && url.startsWith("/uploads/") ? `${apiBase}${url}` : url;

const isLocalFileUrl = (url = "") => !!url && url.startsWith("/uploads/");

const isDriveUrl = (url = "") =>
  !!url &&
  /(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(url);

const isValidURL = (value = "") => {
  const clean = String(value || "").trim();

  if (!clean) return true;

  if (
    clean.startsWith("/uploads") ||
    clean.startsWith("data:") ||
    clean.startsWith("blob:")
  ) {
    return true;
  }

  try {
    const url = new URL(clean);
    return !!url.protocol && !!url.hostname;
  } catch {
    return false;
  }
};

const orNull = (value) => {
  const clean = String(value ?? "").trim();
  return clean === "" ? null : clean;
};

const getAdherentName = (adherent) => {
  if (!adherent) return "Aucun adhérent";

  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const getAdherentPhoto = (adherent) => {
  if (!adherent) return DEFAULT_AVATAR;

  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  const raw =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.avatar ||
    "";

  if (!raw) return getDefaultPhoto(adherent?.genre);

  return toPublicUrl(raw);
};

const extractUploadedUrl = (response, fieldName) => {
  return (
    response?.[fieldName] ||
    response?.url ||
    response?.path ||
    response?.file_url ||
    response?.data?.[fieldName] ||
    response?.data?.url ||
    response?.data?.path ||
    ""
  );
};

function SectionCard({ icon: Icon, title, description, children, className = "" }) {
  return (
    <section
      className={`relative rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80 ${className}`}
    >
      <div className="rounded-t-2xl border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function ValidationSwitch({
  checked,
  onChange,
  label,
  description,
  icon: Icon,
  disabled,
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`flex min-h-[86px] items-center gap-3 rounded-2xl p-4 text-left ring-1 transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
        checked
          ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-900"
      }`}
    >
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${
          checked
            ? "bg-emerald-600 text-white"
            : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        }`}
      >
        {checked ? <FaCheckCircle /> : <Icon />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold">{label}</span>
        <span className="mt-1 block text-xs leading-relaxed opacity-75">
          {description}
        </span>
      </span>

      <span
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <span
          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </span>
    </button>
  );
}

function ModeToggle({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
      <button
        type="button"
        onClick={() => onChange("upload")}
        disabled={disabled}
        className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition disabled:opacity-60 ${
          value === "upload"
            ? "bg-violet-600 text-white shadow-sm"
            : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        }`}
      >
        <FaUpload />
        Fichier
      </button>

      <button
        type="button"
        onClick={() => onChange("link")}
        disabled={disabled}
        className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition disabled:opacity-60 ${
          value === "link"
            ? "bg-violet-600 text-white shadow-sm"
            : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        }`}
      >
        <FaLink />
        Lien
      </button>
    </div>
  );
}

function CurrentDoc({
  label,
  url,
  onOpen,
  onDelete,
  onReplaceClick,
  replacing,
  disabled,
}) {
  const hasDocument = !!url;

  const Icon = hasDocument
    ? isDriveUrl(url)
      ? FaGoogleDrive
      : FaFileAlt
    : FaTimes;

  const badgeLabel = hasDocument
    ? isLocalFileUrl(url)
      ? "Fichier serveur"
      : isDriveUrl(url)
      ? "Google Drive"
      : "Lien externe"
    : "Aucun document";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 ring-1 ring-slate-100 dark:border-slate-800 dark:bg-slate-950/60 dark:ring-slate-800">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${
              hasDocument
                ? "bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-200"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800"
            }`}
          >
            <Icon />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {label}
            </p>

            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {badgeLabel}
            </p>

            {hasDocument && (
              <p className="mt-1 truncate text-xs text-slate-400">
                {url}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onOpen}
            disabled={!hasDocument || disabled}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FaExternalLinkAlt />
            Ouvrir
          </button>

          {hasDocument && (
            <button
              type="button"
              onClick={onDelete}
              disabled={disabled}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 ring-1 ring-rose-100 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-rose-950/30 dark:text-rose-300 dark:ring-rose-900 dark:hover:bg-rose-950/50"
            >
              <FaTrashAlt />
              Supprimer
            </button>
          )}
        </div>
      </div>

      {hasDocument && !replacing && (
        <button
          type="button"
          onClick={onReplaceClick}
          disabled={disabled}
          className="mt-3 text-xs font-bold text-violet-700 underline decoration-dotted underline-offset-4 hover:text-violet-900 disabled:opacity-50 dark:text-violet-300"
        >
          Remplacer par un nouveau document
        </button>
      )}
    </div>
  );
}

function FileUploadBox({
  file,
  onFileChange,
  disabled,
  uploading,
  label = "Fichier PDF/JPG/PNG/WEBP",
}) {
  const inputRef = useRef(null);

  return (
    <div className="rounded-3xl border-2 border-dashed border-violet-200 bg-slate-50 p-4 text-center transition hover:border-violet-400 hover:bg-violet-50/60 dark:border-violet-900 dark:bg-slate-950/40 dark:hover:bg-violet-950/20">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/*"
        onChange={(event) => onFileChange(event.target.files?.[0] || null)}
        className="hidden"
        disabled={disabled}
      />

      <FaCloudUploadAlt className="mx-auto text-4xl text-violet-600 dark:text-violet-300" />

      <p className="mt-2 text-sm font-bold text-slate-800 dark:text-white">
        {file ? file.name : label}
      </p>

      <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        {file
          ? "Le fichier est prêt à être envoyé."
          : "Cliquez pour choisir un document ou une image."}
      </p>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {uploading ? (
          <>
            <FaSpinner className="animate-spin" />
            Upload…
          </>
        ) : (
          <>
            <FaUpload />
            Choisir un fichier
          </>
        )}
      </button>
    </div>
  );
}

function UrlInput({ label, value, onChange, disabled, placeholder = "https://…" }) {
  const invalid = value && !isValidURL(value);

  return (
    <div>
      <label className={labelClass}>{label}</label>

      <div className="relative">
        <FaLink className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`${inputClass} pl-9 ${
            invalid
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100 dark:border-rose-800 dark:focus:ring-rose-950/60"
              : ""
          }`}
        />
      </div>

      {invalid && (
        <p className="mt-1 text-xs font-semibold text-rose-600 dark:text-rose-300">
          URL invalide. Utilisez un lien complet ou laissez vide.
        </p>
      )}
    </div>
  );
}

function DocumentEditor({
  mode,
  setMode,
  file,
  setFile,
  url,
  setUrl,
  onSubmit,
  uploading,
  disabled,
  needsDate,
  dateCm,
}) {
  return (
    <div className="mt-4 space-y-4 rounded-3xl bg-slate-50 p-4 ring-1 ring-slate-100 dark:bg-slate-950/50 dark:ring-slate-800">
      <ModeToggle value={mode} onChange={setMode} disabled={disabled} />

      {mode === "upload" ? (
        <FileUploadBox
          file={file}
          onFileChange={setFile}
          disabled={disabled}
          uploading={uploading}
        />
      ) : (
        <UrlInput
          label="Lien du document"
          value={url}
          onChange={setUrl}
          disabled={disabled}
        />
      )}

      {needsDate && !dateCm && (
        <div className="rounded-2xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 ring-1 ring-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900">
          La date du certificat est obligatoire pour enregistrer cette pièce.
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={
          disabled ||
          uploading ||
          (mode === "upload" && !file) ||
          (mode === "link" && (!url || !isValidURL(url))) ||
          (needsDate && !dateCm)
        }
        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {uploading ? (
          <>
            <FaSpinner className="animate-spin" />
            Enregistrement…
          </>
        ) : (
          <>
            <FaSave />
            Enregistrer ce document
          </>
        )}
      </button>
    </div>
  );
}

function DossierPreviewCard({
  dossier,
  adherent,
  validateCM,
  validateLic,
  validateRegl,
  cmUrl,
  formSigneeUrl,
  licenceUrl,
  driveUrl,
}) {
  const validCount = [validateCM, validateLic, validateRegl].filter(Boolean).length;
  const docCount = [cmUrl, formSigneeUrl, licenceUrl, driveUrl].filter(Boolean).length;

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaFolderOpen className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Dossier inscription
            </p>
            <p className="truncate text-lg font-bold">
              Dossier #{dossier?.id || "—"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col items-center text-center">
          <div className="h-32 w-32 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900">
            {adherent ? (
              <img
                src={getAdherentPhoto(adherent)}
                alt={getAdherentName(adherent)}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
                <FaUser className="text-3xl" />
                <span className="px-3 text-xs font-semibold">Adhérent</span>
              </div>
            )}
          </div>

          <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
            {getAdherentName(adherent)}
          </h3>

          {adherent?.saison_active && (
            <span className="mt-2 inline-flex rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-800/60">
              Saison {adherent.saison_active}
            </span>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-[1.5rem] bg-slate-950 p-4 text-white shadow-lg dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
            <p className="text-xs font-bold uppercase tracking-wide text-white/50">
              Validations
            </p>
            <p className="mt-1 text-3xl font-bold">{validCount}/3</p>
          </div>

          <div className="rounded-[1.5rem] bg-violet-600 p-4 text-white shadow-lg shadow-violet-600/20">
            <p className="text-xs font-bold uppercase tracking-wide text-white/60">
              Documents
            </p>
            <p className="mt-1 text-3xl font-bold">{docCount}</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <PreviewLine
            label="Certificat médical"
            value={validateCM ? "Validé" : "Non validé"}
          />
          <PreviewLine
            label="Licence"
            value={validateLic ? "Validée" : "Non validée"}
          />
          <PreviewLine
            label="Règlement"
            value={validateRegl ? "Validé" : "Non validé"}
          />
        </div>
      </div>
    </section>
  );
}

export default function EditDossierInscriptionModal({
  isOpen,
  onClose,
  dossier,
  refreshList,
}) {
  const dialogRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({
    cm: false,
    signee: false,
    lic: false,
  });

  const [dateCm, setDateCm] = useState("");
  const [validateCM, setValidateCM] = useState(false);
  const [validateLic, setValidateLic] = useState(false);
  const [validateRegl, setValidateRegl] = useState(false);

  const [cmUrl, setCmUrl] = useState("");
  const [formSignerUrl, setFormSignerUrl] = useState("");
  const [formSigneeUrl, setFormSigneeUrl] = useState("");
  const [licenceUrl, setLicenceUrl] = useState("");
  const [driveUrl, setDriveUrl] = useState("");

  const [cmMode, setCmMode] = useState("upload");
  const [cmFile, setCmFile] = useState(null);
  const [cmReplacing, setCmReplacing] = useState(false);

  const [signeeMode, setSigneeMode] = useState("upload");
  const [signeeFile, setSigneeFile] = useState(null);
  const [signeeReplacing, setSigneeReplacing] = useState(false);

  const [licMode, setLicMode] = useState("upload");
  const [licFile, setLicFile] = useState(null);
  const [licReplacing, setLicReplacing] = useState(false);

  const adherent = dossier?.Adherent || null;
  const adherentName = getAdherentName(adherent);

  const busy = saving || uploading.cm || uploading.signee || uploading.lic;

  const hadCmInitial = !!dossier?.cm_url;
  const hadSigneeInitial = !!dossier?.formulaire_licence_signe;
  const hadLicInitial = !!dossier?.licence_url;

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose?.();
  }, [busy, onClose]);

  useEffect(() => {
    if (!isOpen || !dossier) return;

    setCmUrl(dossier.cm_url || "");
    setDateCm(
      dossier.date_cm
        ? new Date(dossier.date_cm).toISOString().slice(0, 10)
        : ""
    );
    setValidateCM(!!dossier.validate_cm);

    setFormSignerUrl(dossier.formulaire_licence_a_signer || "");
    setFormSigneeUrl(dossier.formulaire_licence_signe || "");
    setLicenceUrl(dossier.licence_url || "");
    setDriveUrl(dossier.lien_drive || "");

    setValidateLic(!!dossier.validate_licence);
    setValidateRegl(!!dossier.validate_reglement);

    setCmMode(
      dossier.cm_url && isLocalFileUrl(dossier.cm_url) ? "upload" : "link"
    );
    setSigneeMode(
      dossier.formulaire_licence_signe &&
        isLocalFileUrl(dossier.formulaire_licence_signe)
        ? "upload"
        : "link"
    );
    setLicMode(
      dossier.licence_url && isLocalFileUrl(dossier.licence_url)
        ? "upload"
        : "link"
    );

    setCmFile(null);
    setSigneeFile(null);
    setLicFile(null);

    setCmReplacing(false);
    setSigneeReplacing(false);
    setLicReplacing(false);

    setSaving(false);
    setUploading({
      cm: false,
      signee: false,
      lic: false,
    });
  }, [isOpen, dossier]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !dossier?.id) return null;

  const openUrl = (url) => {
    const publicUrl = toPublicUrl(url);

    if (!publicUrl) return;

    window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;

    try {
      setSaving(true);

      await deleteDossierFile(dossier.id, field);

      if (field === "cm_url") setCmUrl("");
      if (field === SIGNEE_FIELD) setFormSigneeUrl("");
      if (field === "licence_url") setLicenceUrl("");
      if (field === "formulaire_licence_a_signer") setFormSignerUrl("");
      if (field === "lien_drive") setDriveUrl("");

      toast.success("Document supprimé.");

      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  const submitCM = async () => {
    if (!dateCm) {
      toast.error("Indiquez la date exacte du certificat.");
      return;
    }

    try {
      setUploading((previous) => ({
        ...previous,
        cm: true,
      }));

      if (cmMode === "upload") {
        if (!cmFile) {
          toast.error("Choisissez un fichier PDF/JPG/PNG/WEBP.");
          return;
        }

        const data = await uploadCertificatMedical(dossier.id, cmFile, {
          date_cm: dateCm,
        });

        setCmUrl(extractUploadedUrl(data, "cm_url"));
        setDateCm(
          data?.date_cm
            ? new Date(data.date_cm).toISOString().slice(0, 10)
            : dateCm
        );

        toast.success("Certificat médical téléversé.");
      } else {
        if (cmUrl && !isValidURL(cmUrl)) {
          toast.error("URL du certificat médical invalide.");
          return;
        }

        if (isLocalFileUrl(dossier.cm_url)) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch {}
        }

        await updateDossierInscription(dossier.id, {
          cm_url: orNull(cmUrl),
          date_cm: dateCm,
        });

        toast.success("Lien et date du certificat enregistrés.");
      }

      setCmReplacing(false);
      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec sur le certificat médical.");
    } finally {
      setUploading((previous) => ({
        ...previous,
        cm: false,
      }));
    }
  };

  const submitFormSigner = async () => {
    if (formSignerUrl && !isValidURL(formSignerUrl)) {
      toast.error("URL du formulaire à signer invalide.");
      return;
    }

    try {
      setSaving(true);

      await updateDossierInscription(dossier.id, {
        formulaire_licence_a_signer: orNull(formSignerUrl),
      });

      toast.success("Lien du formulaire à signer mis à jour.");
      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec de la mise à jour du formulaire à signer.");
    } finally {
      setSaving(false);
    }
  };

  const submitSignee = async () => {
    try {
      setUploading((previous) => ({
        ...previous,
        signee: true,
      }));

      if (signeeMode === "upload") {
        if (!signeeFile) {
          toast.error("Choisissez un fichier PDF/JPG/PNG/WEBP.");
          return;
        }

        const data = await uploadFormulaireLicenceSigne(
          dossier.id,
          signeeFile
        );

        const newUrl = extractUploadedUrl(data, SIGNEE_FIELD);

        if (newUrl) {
          setFormSigneeUrl(newUrl);
        }

        toast.success("Formulaire signé téléversé.");
      } else {
        if (formSigneeUrl && !isValidURL(formSigneeUrl)) {
          toast.error("URL du formulaire signé invalide.");
          return;
        }

        if (isLocalFileUrl(dossier.formulaire_licence_signe)) {
          try {
            await deleteDossierFile(dossier.id, SIGNEE_FIELD);
          } catch {}
        }

        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: orNull(formSigneeUrl),
        });

        toast.success("Lien du formulaire signé enregistré.");
      }

      setSigneeReplacing(false);
      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec sur le formulaire signé.");
    } finally {
      setUploading((previous) => ({
        ...previous,
        signee: false,
      }));
    }
  };

  const submitLicence = async () => {
    try {
      setUploading((previous) => ({
        ...previous,
        lic: true,
      }));

      if (licMode === "upload") {
        if (!licFile) {
          toast.error("Choisissez un fichier PDF/JPG/PNG/WEBP.");
          return;
        }

        const data = await uploadLicenceDefinitive(dossier.id, licFile);

        setLicenceUrl(extractUploadedUrl(data, "licence_url"));

        toast.success("Licence définitive téléversée.");
      } else {
        if (licenceUrl && !isValidURL(licenceUrl)) {
          toast.error("URL de la licence invalide.");
          return;
        }

        if (isLocalFileUrl(dossier.licence_url)) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch {}
        }

        await updateDossierInscription(dossier.id, {
          licence_url: orNull(licenceUrl),
        });

        toast.success("Lien de la licence enregistré.");
      }

      setLicReplacing(false);
      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec sur la licence.");
    } finally {
      setUploading((previous) => ({
        ...previous,
        lic: false,
      }));
    }
  };

  const submitDrive = async () => {
    if (driveUrl && !isValidURL(driveUrl)) {
      toast.error("URL du dossier Drive invalide.");
      return;
    }

    try {
      setSaving(true);

      await updateDossierInscription(dossier.id, {
        lien_drive: orNull(driveUrl),
      });

      toast.success("Lien Drive mis à jour.");
      await refreshList?.();
    } catch (error) {
      console.error(error);
      toast.error("Échec de la mise à jour du lien Drive.");
    } finally {
      setSaving(false);
    }
  };

  const submitMeta = async (event) => {
    event?.preventDefault?.();

    if (formSignerUrl && !isValidURL(formSignerUrl)) {
      toast.error("URL du formulaire à signer invalide.");
      return;
    }

    if (formSigneeUrl && !isValidURL(formSigneeUrl)) {
      toast.error("URL du formulaire signé invalide.");
      return;
    }

    if (driveUrl && !isValidURL(driveUrl)) {
      toast.error("URL du dossier Drive invalide.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        validate_cm: !!validateCM,
        validate_licence: !!validateLic,
        validate_reglement: !!validateRegl,
        formulaire_licence_a_signer: orNull(formSignerUrl),
        formulaire_licence_signe: orNull(formSigneeUrl),
        lien_drive: orNull(driveUrl),
        date_cm: validateCM ? dateCm || todayStr() : null,
      };

      await updateDossierInscription(dossier.id, payload);

      toast.success("Dossier mis à jour.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleValidateCM = () => {
    setValidateCM((previous) => {
      const next = !previous;

      if (next && !dateCm) {
        setDateCm(todayStr());
      }

      return next;
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Éditer un dossier d’inscription"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative flex max-h-[94dvh] w-full max-w-7xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Dossiers inscriptions
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl pb-6">
                Modifier un dossier d’inscription
              </h3>

              
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={submitMeta} className="flex min-h-0 flex-1 flex-col">
          <div className="edit-dossier-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <DossierPreviewCard
                  dossier={dossier}
                  adherent={adherent}
                  validateCM={validateCM}
                  validateLic={validateLic}
                  validateRegl={validateRegl}
                  cmUrl={cmUrl}
                  formSigneeUrl={formSigneeUrl}
                  licenceUrl={licenceUrl}
                  driveUrl={driveUrl}
                />

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Contrôle rapide avant enregistrement."
                >
                  <div className="space-y-3">
                    <PreviewLine label="Adhérent" value={adherentName} />
                    <PreviewLine
                      label="Certificat"
                      value={validateCM ? "Validé" : "Non validé"}
                    />
                    <PreviewLine
                      label="Licence"
                      value={validateLic ? "Validée" : "Non validée"}
                    />
                    <PreviewLine
                      label="Règlement"
                      value={validateRegl ? "Validé" : "Non validé"}
                    />
                  </div>

                  <div className="mt-4 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
                    Le bouton final enregistre les validations, la date CM et les
                    liens principaux. Chaque document peut aussi être enregistré
                    séparément.
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaUser}
                  title="Adhérent"
                  description="Le dossier est rattaché à cet adhérent."
                >
                  <div className="flex items-center gap-3 rounded-2xl bg-violet-50 p-3 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:ring-violet-800/60">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-white ring-1 ring-violet-100 dark:bg-slate-950 dark:ring-violet-800">
                      <img
                        src={getAdherentPhoto(adherent)}
                        alt={adherentName}
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-violet-900 dark:text-violet-100">
                        {adherentName}
                      </p>

                      <p className="text-xs text-violet-700/75 dark:text-violet-200/75">
                        Dossier #{dossier.id}
                        {adherent?.saison_active
                          ? ` · Saison ${adherent.saison_active}`
                          : ""}
                      </p>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaFileMedical}
                  title="Certificat médical"
                  description="Gérez le certificat médical, sa date et sa validation."
                >
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Date du certificat</label>

                      <div className="relative">
                        <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="date"
                          value={dateCm || ""}
                          onChange={(event) => setDateCm(event.target.value)}
                          disabled={busy}
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>

                    <ValidationSwitch
                      checked={!!validateCM}
                      onChange={toggleValidateCM}
                      label="Certificat médical validé"
                      description="Si aucune date n’est renseignée, la date du jour sera ajoutée."
                      icon={FaFileMedical}
                      disabled={busy}
                    />

                    <CurrentDoc
                      label="Document actuel"
                      url={cmUrl}
                      onOpen={() => openUrl(cmUrl)}
                      onDelete={() => removeField("cm_url", "Certificat médical")}
                      onReplaceClick={() => setCmReplacing(true)}
                      replacing={cmReplacing}
                      disabled={busy}
                    />

                    {(!hadCmInitial || cmReplacing) && (
                      <DocumentEditor
                        mode={cmMode}
                        setMode={setCmMode}
                        file={cmFile}
                        setFile={setCmFile}
                        url={cmUrl}
                        setUrl={setCmUrl}
                        onSubmit={submitCM}
                        uploading={uploading.cm}
                        disabled={busy}
                        needsDate
                        dateCm={dateCm}
                      />
                    )}
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaFileSignature}
                  title="Licence et assurance"
                  description="Formulaire à signer, formulaire signé et licence définitive."
                >
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <CurrentDoc
                        label="Formulaire à signer"
                        url={formSignerUrl}
                        onOpen={() => openUrl(formSignerUrl)}
                        onDelete={() =>
                          removeField(
                            "formulaire_licence_a_signer",
                            "Formulaire à signer"
                          )
                        }
                        onReplaceClick={() => {}}
                        replacing={false}
                        disabled={busy}
                      />

                      <UrlInput
                        label="Lien du formulaire à signer"
                        value={formSignerUrl}
                        onChange={setFormSignerUrl}
                        disabled={busy}
                        placeholder="https://drive.google.com/…"
                      />

                      <button
                        type="button"
                        onClick={submitFormSigner}
                        disabled={busy || (formSignerUrl && !isValidURL(formSignerUrl))}
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                      >
                        {saving ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Enregistrement…
                          </>
                        ) : (
                          <>
                            <FaSave />
                            Enregistrer ce lien
                          </>
                        )}
                      </button>
                    </div>

                    <div className="space-y-3">
                      <CurrentDoc
                        label="Formulaire signé"
                        url={formSigneeUrl}
                        onOpen={() => openUrl(formSigneeUrl)}
                        onDelete={() =>
                          removeField(SIGNEE_FIELD, "Formulaire signé")
                        }
                        onReplaceClick={() => setSigneeReplacing(true)}
                        replacing={signeeReplacing}
                        disabled={busy}
                      />

                      {(!hadSigneeInitial || signeeReplacing) && (
                        <DocumentEditor
                          mode={signeeMode}
                          setMode={setSigneeMode}
                          file={signeeFile}
                          setFile={setSigneeFile}
                          url={formSigneeUrl}
                          setUrl={setFormSigneeUrl}
                          onSubmit={submitSignee}
                          uploading={uploading.signee}
                          disabled={busy}
                        />
                      )}
                    </div>

                    <div className="space-y-3">
                      <CurrentDoc
                        label="Licence / Assurance"
                        url={licenceUrl}
                        onOpen={() => openUrl(licenceUrl)}
                        onDelete={() => removeField("licence_url", "Licence")}
                        onReplaceClick={() => setLicReplacing(true)}
                        replacing={licReplacing}
                        disabled={busy}
                      />

                      {(!hadLicInitial || licReplacing) && (
                        <DocumentEditor
                          mode={licMode}
                          setMode={setLicMode}
                          file={licFile}
                          setFile={setLicFile}
                          url={licenceUrl}
                          setUrl={setLicenceUrl}
                          onSubmit={submitLicence}
                          uploading={uploading.lic}
                          disabled={busy}
                        />
                      )}
                    </div>

                    <ValidationSwitch
                      checked={!!validateLic}
                      onChange={() => setValidateLic((previous) => !previous)}
                      label="Licence validée"
                      description="Indique que la partie licence / assurance est conforme."
                      icon={FaIdCard}
                      disabled={busy}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaGoogleDrive}
                  title="Dossier Drive"
                  description="Lien vers le dossier partagé si vous en utilisez un."
                >
                  <div className="space-y-3">
                    <CurrentDoc
                      label="Dossier Google Drive"
                      url={driveUrl}
                      onOpen={() => openUrl(driveUrl)}
                      onDelete={() => removeField("lien_drive", "Lien Drive")}
                      onReplaceClick={() => {}}
                      replacing={false}
                      disabled={busy}
                    />

                    <UrlInput
                      label="Lien Drive"
                      value={driveUrl}
                      onChange={setDriveUrl}
                      disabled={busy}
                      placeholder="https://drive.google.com/…"
                    />

                    <button
                      type="button"
                      onClick={submitDrive}
                      disabled={busy || (driveUrl && !isValidURL(driveUrl))}
                      className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                    >
                      {saving ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Enregistrement…
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Enregistrer le lien Drive
                        </>
                      )}
                    </button>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaClipboardCheck}
                  title="Validations du dossier"
                  description="Indiquez les éléments déjà validés."
                >
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                    <ValidationSwitch
                      checked={!!validateCM}
                      onChange={toggleValidateCM}
                      label="Certificat médical"
                      description="CM vérifié et validé."
                      icon={FaFileMedical}
                      disabled={busy}
                    />

                    <ValidationSwitch
                      checked={!!validateLic}
                      onChange={() => setValidateLic((previous) => !previous)}
                      label="Licence"
                      description="Licence ou formulaire validé."
                      icon={FaFileSignature}
                      disabled={busy}
                    />

                    <ValidationSwitch
                      checked={!!validateRegl}
                      onChange={() => setValidateRegl((previous) => !previous)}
                      label="Règlement"
                      description="Paiement ou règlement validé."
                      icon={FaShieldAlt}
                      disabled={busy}
                    />
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={busy}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <FaSave />
                    Enregistrer les validations
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .edit-dossier-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .edit-dossier-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .edit-dossier-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .edit-dossier-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}